import styles from './data-comparison.module.scss';
import { isIgnoredKey, type ComparisonConfig } from './campaign-diff.config';
import {
	buildAddedOrRemovedFields,
	buildArrayItemFields,
	buildMinimalFields,
	getArrayItemContext,
	getByPath,
	getEntityLabel,
	groupChangesByParent,
	groupChangesBySection,
	isPlainObjectValue,
	toPlainRoot,
	type Change,
	type ChangeGroup,
	type MinimalField,
} from './format-comparison-value';

interface IDataComparisonModal {
	changes: Change[];
	config: ComparisonConfig;
	oldRoot?: unknown;
	newRoot?: unknown;
}

const formatArrayOrObject = (val: any, key: string) => {
	if (Array.isArray(val) && val.length > 4) {
		return `${key} size is ${val.length}`;
	}
	return JSON.stringify(val);
};

const SideBySideFields = ({
	fields,
	hideBeforeValues = false,
	hideAfterValues = false,
}: {
	fields: MinimalField[];
	hideBeforeValues?: boolean;
	hideAfterValues?: boolean;
}) => {
	const visibleFields = fields.filter(field => field.changed);
	if (visibleFields.length === 0) {
		return null;
	}

	return (
		<div className={styles.sideBySide}>
			<div className={styles.column}>
				<div className={styles.columnHeader}>Before</div>
				{!hideBeforeValues &&
					visibleFields.map(field => (
						<div key={`old-${field.key}`} className={styles.changedOld}>
							{field.key}: {field.oldDisplay}
						</div>
					))}
			</div>
			<div className={styles.column}>
				<div className={styles.columnHeader}>After</div>
				{!hideAfterValues &&
					visibleFields.map(field => (
						<div key={`new-${field.key}`} className={styles.changedNew}>
							{field.key}: {field.newDisplay}
						</div>
					))}
			</div>
		</div>
	);
};

const resolveGroupFields = (
	group: ChangeGroup,
	oldPlain: Record<string, any> | null,
	newPlain: Record<string, any> | null,
	config: ComparisonConfig
): MinimalField[] => {
	if (group.kind === 'add') {
		return buildAddedOrRemovedFields(group.changes[0]?.new, group.parentPath, 'add', config);
	}
	if (group.kind === 'remove') {
		return buildAddedOrRemovedFields(group.changes[0]?.old, group.parentPath, 'remove', config);
	}

	const itemContext = getArrayItemContext(group.parentPath, config);
	if (itemContext) {
		const oldItem = getByPath(oldPlain, itemContext.itemPath);
		const newItem = getByPath(newPlain, itemContext.itemPath);
		return buildArrayItemFields(itemContext, oldItem, newItem, group.changes);
	}

	const oldObj = getByPath(oldPlain, group.parentPath);
	const newObj = getByPath(newPlain, group.parentPath);
	const parentIsObject = isPlainObjectValue(oldObj) || isPlainObjectValue(newObj);

	if (parentIsObject || group.leafKeys.length > 0) {
		return buildMinimalFields(oldObj, newObj, group.parentPath, group.leafKeys, group.changes, config.ignoreKeys);
	}

	return group.changes.map(change => {
		let oldValue = change.old;
		let newValue = change.new;

		if (typeof oldValue === 'object' || typeof newValue === 'object') {
			oldValue = oldValue != null && typeof oldValue === 'object' ? formatArrayOrObject(oldValue, change.key) : oldValue;
			newValue = newValue != null && typeof newValue === 'object' ? formatArrayOrObject(newValue, change.key) : newValue;
		}

		return {
			key: change.key,
			oldDisplay: oldValue === '' || oldValue === undefined ? 'Not specified' : String(oldValue),
			newDisplay: newValue === '' || newValue === undefined ? 'Not specified' : String(newValue),
			changed: true,
		};
	});
};

const resolveGroupHeader = (
	group: ChangeGroup,
	oldPlain: Record<string, any> | null,
	newPlain: Record<string, any> | null,
	config: ComparisonConfig
): string => {
	if (group.kind === 'remove') {
		return getEntityLabel(oldPlain, group, config) ?? group.parentPath;
	}
	return getEntityLabel(newPlain, group, config) ?? getEntityLabel(oldPlain, group, config) ?? group.parentPath;
};

export const DataComparisonModal = (props: IDataComparisonModal) => {
	const { changes, config, oldRoot, newRoot } = props;
	const filteredChanges = changes.filter(change => !isIgnoredKey(change.key, config.ignoreKeys));
	const groups = groupChangesByParent(filteredChanges, config);
	const sections = groupChangesBySection(groups, config);
	const oldPlain = toPlainRoot(oldRoot);
	const newPlain = toPlainRoot(newRoot);
	const visibleSections = sections
		.map(section => ({
			title: section.title,
			groups: section.groups
				.map(group => ({ group, fields: resolveGroupFields(group, oldPlain, newPlain, config) }))
				.filter(({ fields }) => fields.some(field => field.changed)),
		}))
		.filter(section => section.groups.length > 0);

	return (
		<div className={styles.wrapper}>
			<div>Be aware of recent changes. See the comparison below.</div>
			{visibleSections.map(section => (
				<div key={section.title} className={styles.sectionBucket}>
					<div className={styles.sectionTitle}>{section.title}</div>
					{section.groups.map(({ group, fields }) => {
						const header = resolveGroupHeader(group, oldPlain, newPlain, config);
						const badge =
							group.kind === 'add' ? (
								<span className={styles.added}>New</span>
							) : group.kind === 'remove' ? (
								<span className={styles.removed}>Removed</span>
							) : null;

						return (
							<div key={`${group.kind}-${group.parentPath}`} className={styles.changeWrapper}>
								<div className={styles.groupHeader}>
									<span>{header}</span>
									{badge}
								</div>
								<SideBySideFields
									fields={fields}
									hideBeforeValues={group.kind === 'add'}
									hideAfterValues={group.kind === 'remove'}
								/>
							</div>
						);
					})}
				</div>
			))}
		</div>
	);
};
